import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList,
} from "recharts";
import {
    Building2, Users, ChevronRight, IndianRupee, Crown, TrendingUp,
    CreditCard, AlertTriangle, Check, Clock, X, Ban,
} from "lucide-react";
import { StatCard, inr, GoldDivider, CHOCOLATE, GOLD } from "@/components/platform/platformUi";

const SUBSCRIPTION_SERIES = [
    {
        key: "active",
        label: "Active",
        color: "#2D9B6E",
        gradient: ["#4BC492", "#2D9B6E"],
        icon: Check,
    },
    {
        key: "trial",
        label: "Trial",
        color: "#3B82C4",
        gradient: ["#6BA3E0", "#3B82C4"],
        icon: Clock,
    },
    {
        key: "past_due",
        label: "Past due",
        color: "#C84B31",
        gradient: ["#E06B52", "#C84B31"],
        icon: AlertTriangle,
    },
    {
        key: "cancelled",
        label: "Cancelled",
        color: "#B8859C",
        gradient: ["#CEA3B5", "#A87088"],
        icon: Ban,
    },
    {
        key: "expired",
        label: "Expired",
        color: "#8A8178",
        gradient: ["#A89E95", "#706B66"],
        icon: X,
    },
];

function getGreeting() {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return "—";
    }
}

function fmtTrendDay(dateStr) {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString("en-GB", { weekday: "short" });
}

function PlatformHeader({ firstName }) {
    const greeting = getGreeting();
    const now = new Date();
    const weekday = now.toLocaleDateString("en-GB", { weekday: "long" });
    const dayMonth = now.toLocaleDateString("en-GB", { day: "numeric", month: "long" });

    return (
        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
                <p className="text-[1.25rem] font-semibold leading-snug tracking-tight sm:text-[1.375rem]">
                    <span style={{ color: CHOCOLATE }}>{weekday}</span>{" "}
                    <span style={{ color: GOLD }}>{dayMonth}</span>
                </p>
                <h1 className="mt-1.5 text-[1.375rem] font-semibold leading-snug sm:text-[1.5rem]">
                    <span style={{ color: CHOCOLATE }}>{greeting},</span>{" "}
                    <span style={{ color: GOLD }}>{firstName}</span>
                </h1>
                <GoldDivider className="mt-3 max-w-[200px]" />
            </div>
            <div className="flex w-full max-w-[200px] flex-col sm:ml-auto">
                <p className="text-[1.25rem] font-semibold leading-snug sm:text-right sm:text-[1.375rem]">
                    <span style={{ color: CHOCOLATE }}>Platform</span>
                </p>
                <div className="mt-2.5 flex w-full gap-2">
                    <Link
                        to="/platform/salons"
                        className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border border-[#E8E4DE] bg-white px-2.5 text-[11px] font-semibold text-[#3D2817] shadow-sm transition-colors hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]"
                    >
                        <Building2 className="size-3.5 shrink-0 text-[#D4AF37]" strokeWidth={2.25} />
                        Salons
                    </Link>
                    <Link
                        to="/platform/billing"
                        className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border border-[#E8E4DE] bg-white px-2.5 text-[11px] font-semibold text-[#3D2817] shadow-sm transition-colors hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]"
                    >
                        <IndianRupee className="size-3.5 shrink-0 text-[#D4AF37]" strokeWidth={2.25} />
                        Billing
                    </Link>
                </div>
                <GoldDivider className="mt-3 w-full" />
            </div>
        </header>
    );
}

function RevenueDot({ cx, cy }) {
    if (cx == null || cy == null) return null;
    return (
        <g>
            <circle cx={cx} cy={cy} r={6} fill="#FFF8E7" stroke="#4A0E17" strokeWidth={2} />
            <circle cx={cx} cy={cy} r={2.5} fill="#D4AF37" />
        </g>
    );
}

function RevenuePointLabel({ x, y, value }) {
    if (x == null || y == null || value == null) return null;
    return (
        <text x={x} y={y - 14} textAnchor="middle" fill="#4A0E17" fontSize={11} fontWeight={700}>
            {inr(value)}
        </text>
    );
}

function PlatformRevenueChart({ trend }) {
    const chartHeight = 280;
    const points = (trend || []).length || 7;
    const chartScrollWidth = Math.max(680, points * 88);
    const chartData = (trend || []).map((d) => ({ ...d, label: fmtTrendDay(d.date) }));

    const chart = (
        <ResponsiveContainer width={chartScrollWidth} height={chartHeight}>
            <AreaChart data={chartData} margin={{ top: 24, right: 28, left: 4, bottom: 0 }}>
                <defs>
                    <linearGradient id="platformRevenueFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4A0E17" stopOpacity={0.32} />
                        <stop offset="100%" stopColor="#4A0E17" stopOpacity={0} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke="#E8E4DE" vertical={false} />
                <XAxis
                    dataKey="label"
                    stroke="#8A8178"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    dy={8}
                />
                <YAxis
                    domain={[0, "auto"]}
                    stroke="#8A8178"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => (v >= 1000 ? `₹${v / 1000}K` : `₹${v}`)}
                    width={48}
                />
                <Tooltip
                    contentStyle={{
                        background: "#fff",
                        border: "1px solid #E8E4DE",
                        borderRadius: 8,
                        fontSize: 12,
                        color: "#4A0E17",
                    }}
                    formatter={(v) => [inr(v), "Revenue"]}
                    labelStyle={{ color: "#706B66", fontWeight: 700 }}
                />
                <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#4A0E17"
                    strokeWidth={2.5}
                    fill="url(#platformRevenueFill)"
                    dot={<RevenueDot />}
                    activeDot={false}
                >
                    <LabelList dataKey="revenue" content={<RevenuePointLabel />} />
                </Area>
            </AreaChart>
        </ResponsiveContainer>
    );

    return (
        <div className="relative min-w-0 overflow-hidden rounded-xl border border-[#E8E4DE] bg-white p-6">
            <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
            <div className="mb-6">
                <h2 className="text-xl font-bold text-[#4A0E17] sm:text-2xl">
                    Platform revenue · last 7 days
                </h2>
                <div className="mt-2 flex max-w-[180px] items-center">
                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                    <span className="px-2 text-[10px] leading-none text-[#D4AF37]">◆</span>
                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                </div>
            </div>
            <div className="scrollbar-chart scrollbar-chart-x -mx-1 px-1 pb-3 pt-0.5">
                <div style={{ width: chartScrollWidth, height: chartHeight }}>
                    {chart}
                </div>
            </div>
        </div>
    );
}

function SignupsChart({ trend }) {
    const chartHeight = 280;
    const chartData = (trend || []).map((d) => ({ ...d, label: fmtTrendDay(d.date) }));
    const peak = Math.max(...chartData.map((d) => d.count), 1);

    return (
        <div className="relative flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-[#E8E4DE] bg-white p-6">
            <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
            <div className="mb-6">
                <h2 className="text-xl font-bold text-[#4A0E17] sm:text-2xl">
                    New signups · last 7 days
                </h2>
                <div className="mt-2 flex max-w-[160px] items-center">
                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                    <span className="px-2 text-[10px] leading-none text-[#D4AF37]">◆</span>
                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                </div>
            </div>
            <div className="flex flex-1 flex-col justify-end">
                <div className="flex items-end justify-around gap-2 px-1" style={{ height: chartHeight - 40 }}>
                    {chartData.map((item) => {
                        const barH = peak > 0 ? Math.max(item.count > 0 ? 12 : 0, (item.count / peak) * (chartHeight - 80)) : 0;
                        return (
                            <div key={item.date} className="flex max-w-[72px] flex-1 flex-col items-center">
                                <span className="mb-1.5 text-sm font-extrabold tabular-nums text-[#3B82C4]">
                                    {item.count}
                                </span>
                                <div
                                    className="w-full rounded-t-lg transition-all duration-500"
                                    style={{
                                        height: barH,
                                        background: "linear-gradient(180deg, #6BA3E0 0%, #3B82C4 100%)",
                                        boxShadow: item.count > 0 ? "0 4px 14px rgba(59,130,196,0.25)" : "none",
                                    }}
                                />
                                <span className="mt-3 text-center text-[11px] font-bold text-[#3D2817] sm:text-xs">
                                    {item.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function buildSubscriptionData(breakdown) {
    const counts = Object.fromEntries(
        (breakdown || []).map(({ status, count }) => [status, count])
    );
    return SUBSCRIPTION_SERIES.map((series) => ({
        ...series,
        count: counts[series.key] || 0,
    }));
}

function getStatusYMax(maxCount) {
    if (maxCount <= 0) return 20;
    const step = maxCount <= 10 ? 5 : 10;
    return Math.max(step * 4, Math.ceil(maxCount / step) * step);
}

function SubscriptionStatusChart({ breakdown }) {
    const data = buildSubscriptionData(breakdown);
    const peak = Math.max(...data.map((d) => d.count), 0);
    const yMax = getStatusYMax(peak);
    const yTicks = [0, 1, 2, 3, 4].map((i) => Math.round((yMax / 4) * (4 - i)));
    const PLOT_H = 160;
    const FOOTER_H = 56;

    return (
        <div className="relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white p-6">
            <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
            <div className="relative z-10 mb-6">
                <h2 className="text-xl font-bold text-[#4A0E17] sm:text-2xl">
                    Subscription status
                </h2>
                <div className="mt-2 flex max-w-[220px] items-center">
                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                    <span className="px-2 text-[10px] leading-none text-[#D4AF37]">◆</span>
                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                </div>
            </div>
            <div className="flex gap-3 sm:gap-4">
                <div
                    className="flex w-7 shrink-0 flex-col justify-between pt-0.5 text-[11px] font-semibold tabular-nums text-[#8A8178]"
                    style={{ height: PLOT_H }}
                >
                    {yTicks.map((tick) => (
                        <span key={tick}>{tick}</span>
                    ))}
                </div>
                <div className="relative min-w-0 flex-1">
                    <div
                        className="pointer-events-none absolute inset-x-0 top-0 flex flex-col justify-between"
                        style={{ height: PLOT_H }}
                    >
                        {yTicks.map((tick) => (
                            <div key={tick} className="border-t border-dashed border-[#E8E4DE]" aria-hidden />
                        ))}
                    </div>
                    <div
                        className="pointer-events-none absolute inset-x-0 border-t border-[#E8E4DE]/90"
                        style={{ top: PLOT_H }}
                        aria-hidden
                    />
                    <div
                        className="relative flex justify-around gap-1 px-1 sm:gap-3 sm:px-2"
                        style={{ height: PLOT_H + FOOTER_H }}
                    >
                        {data.map((item) => {
                            const Icon = item.icon;
                            const barH = yMax > 0
                                ? Math.max(item.count > 0 ? 10 : 0, (item.count / yMax) * PLOT_H)
                                : 0;
                            return (
                                <div
                                    key={item.key}
                                    className="flex max-w-[72px] flex-1 flex-col items-center sm:max-w-[80px]"
                                >
                                    <div
                                        className="relative flex w-full flex-col items-center justify-end"
                                        style={{ height: PLOT_H }}
                                    >
                                        <span
                                            className="mb-1.5 text-sm font-extrabold tabular-nums"
                                            style={{ color: item.color }}
                                        >
                                            {item.count}
                                        </span>
                                        <div
                                            className="w-full rounded-t-lg transition-all duration-500"
                                            style={{
                                                height: barH,
                                                background: `linear-gradient(180deg, ${item.gradient[0]} 0%, ${item.gradient[1]} 100%)`,
                                                boxShadow: item.count > 0 ? `0 4px 14px ${item.color}33` : "none",
                                            }}
                                        />
                                        <div
                                            className="absolute bottom-0 left-1/2 z-10 flex size-10 -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full bg-white ring-2 ring-white"
                                            style={{ boxShadow: `0 4px 14px rgba(45,41,38,0.12), 0 0 0 1px ${item.color}22` }}
                                        >
                                            <Icon className="size-[17px]" style={{ color: item.color }} strokeWidth={2.25} />
                                        </div>
                                    </div>
                                    <span className="mt-7 text-center text-[10px] font-bold leading-tight text-[#3D2817] sm:text-xs">
                                        {item.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function PlatformDashboard() {
    const { user } = useAuth();
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/platform/overview")
            .then((r) => setOverview(r.data))
            .catch(() => toast.error("Could not load dashboard"))
            .finally(() => setLoading(false));
    }, []);

    const firstName = user?.full_name?.split(" ")[0] || "Admin";

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="platform-dashboard-page">
            <PlatformHeader firstName={firstName} />

            {loading ? (
                <div className="text-[#8A8178]">Loading dashboard…</div>
            ) : overview ? (
                <>
                    {(overview.expiring_trials_count || 0) > 0 && (
                        <section className="rounded-xl border border-[#F5D88A] bg-[#FFFBF0] p-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="flex items-start gap-3">
                                    <AlertTriangle className="mt-0.5 size-5 shrink-0 text-[#9A7B10]" />
                                    <div>
                                        <p className="text-sm font-bold text-[#4A0E17]">
                                            {overview.expiring_trials_count} trial{overview.expiring_trials_count === 1 ? "" : "s"} ending in 7 days
                                        </p>
                                        <p className="mt-1 text-xs text-[#8A8178]">Follow up before trials expire</p>
                                    </div>
                                </div>
                                <Link to="/platform/subscriptions?trial=active" className="text-sm font-semibold text-[#C9A227] hover:text-[#9A7B10]">
                                    View subscriptions →
                                </Link>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {(overview.expiring_trials_7d || []).slice(0, 5).map((t) => (
                                    <Link
                                        key={t.id}
                                        to={`/platform/salons/${t.id}`}
                                        className="rounded-lg border border-[#E8E4DE] bg-white px-3 py-1.5 text-xs font-semibold text-[#3D2817] hover:border-[#D4AF37]/50"
                                    >
                                        {t.name}
                                    </Link>
                                ))}
                            </div>
                        </section>
                    )}

                    <div className="grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                        <StatCard
                            icon={Building2}
                            iconTone="revenue"
                            label="Total salons"
                            value={overview.total_salons}
                            sub={`${overview.active_salons} active · ${overview.suspended_salons} suspended`}
                            to="/platform/salons"
                        />
                        <StatCard
                            icon={Crown}
                            iconTone="customers"
                            label="MRR"
                            value={inr(overview.mrr)}
                            sub="Monthly recurring revenue"
                            to="/platform/revenue"
                        />
                        <StatCard
                            icon={IndianRupee}
                            iconTone="revenue"
                            label="Platform revenue (30d)"
                            value={inr(overview.platform_revenue_30d)}
                            sub="Subscription payments"
                            to="/platform/billing"
                        />
                        <StatCard
                            icon={CreditCard}
                            iconTone="staff"
                            label="Paid subscriptions"
                            value={overview.paid_subscriptions ?? 0}
                            sub={`${overview.trial_active ?? 0} on trial`}
                            to="/platform/subscriptions"
                        />
                        <StatCard
                            icon={TrendingUp}
                            iconTone="bookings"
                            label="Signups (7d)"
                            value={overview.signups_7d}
                            sub="New salons this week"
                            to="/platform/salons"
                        />
                    </div>

                    <div className="grid min-w-0 items-stretch gap-5 lg:grid-cols-3">
                        <div className="min-w-0 lg:col-span-2">
                            <PlatformRevenueChart trend={overview.revenue_trend} />
                        </div>
                        <div className="min-w-0">
                            <SignupsChart trend={overview.signups_trend} />
                        </div>
                    </div>

                    <SubscriptionStatusChart breakdown={overview.subscription_breakdown} />

                    <section className="relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white">
                        <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
                        <div className="flex items-end justify-between gap-3 border-b border-[#E8E4DE] px-6 py-5">
                            <div>
                                <h2 className="text-xl font-bold text-[#4A0E17] sm:text-2xl">Recent signups</h2>
                                <div className="mt-2 flex max-w-[160px] items-center">
                                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                                    <span className="px-2 text-[10px] leading-none text-[#D4AF37]">◆</span>
                                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                                </div>
                            </div>
                            <Link
                                to="/platform/salons"
                                className="inline-flex items-center gap-1 rounded-full border border-[#E8E4DE] bg-[#FDF8F6] px-4 py-2 text-[13px] font-bold text-[#4A0E17] transition-colors hover:border-[#D4AF37]/40 hover:bg-[#F5F0E8]"
                            >
                                View all salons
                                <ChevronRight className="size-4 text-[#8A8178]" />
                            </Link>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-max w-full border-collapse">
                                <thead>
                                    <tr className="border-b border-[#E8E4DE] text-left text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">
                                        <th className="px-6 py-3">Salon</th>
                                        <th className="px-4 py-3">City</th>
                                        <th className="px-4 py-3">Owner</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3">Trial</th>
                                        <th className="px-4 py-3">Joined</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(overview.recent_signups || []).map((t) => (
                                        <tr key={t.id} className="border-b border-[#E8E4DE]/50 last:border-b-0">
                                            <td className="px-6 py-3">
                                                <Link
                                                    to={`/platform/salons/${t.id}`}
                                                    className="font-semibold text-[#4A0E17] hover:text-[#C9A227]"
                                                >
                                                    {t.name}
                                                </Link>
                                                <div className="text-xs font-medium text-[#8A8178]">{t.slug}</div>
                                            </td>
                                            <td className="px-4 py-3 text-sm font-medium text-[#3D2817]">
                                                {t.city || "—"}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-medium text-[#3D2817]">
                                                {t.owner_email || "—"}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-semibold">
                                                <span
                                                    className={`rounded-full px-2.5 py-0.5 ${
                                                        t.status === "suspended"
                                                            ? "bg-[#FEF5F3] text-[#C84B31]"
                                                            : "bg-[#FDF3DC] text-[#9A7B10]"
                                                    }`}
                                                >
                                                    {t.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm font-semibold text-[#3D2817]">
                                                {t.trial_status || "—"}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-medium text-[#8A8178]">
                                                {formatDate(t.created_at)}
                                            </td>
                                        </tr>
                                    ))}
                                    {(overview.recent_signups || []).length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="px-6 py-10 text-center text-sm font-medium text-[#8A8178]">
                                                No signups yet
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </>
            ) : null}
        </div>
    );
}
